import { Check } from 'lucide-react';

interface StreakCalendarProps {
  weekActivityDays: string[];
  weeklyGoal: number;
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Helper to get Monday of current week
function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d;
}

export function StreakCalendar({ weekActivityDays, weeklyGoal }: StreakCalendarProps) {
  const weekStart = getWeekStart(new Date());
  const today = new Date().toISOString().split('T')[0];

  // Generate dates for this week (Mon-Sun)
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    return date.toISOString().split('T')[0];
  });

  const daysCompleted = weekActivityDays.length;
  const isGoalMet = daysCompleted >= weeklyGoal;

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center text-xs">
        <span className="text-muted-foreground">This week's progress</span>
        <span className={`font-semibold ${isGoalMet ? 'text-green-600 dark:text-green-400' : 'text-foreground'}`}>
          {daysCompleted}/{weeklyGoal} days
        </span>
      </div>
      
      <div className="flex justify-between gap-1">
        {DAYS.map((day, index) => {
          const date = weekDates[index];
          const isActive = weekActivityDays.includes(date);
          const isToday = date === today;
          const isFuture = new Date(date) > new Date();

          return (
            <div key={day} className="flex flex-col items-center gap-1">
              <span className="text-[10px] text-muted-foreground font-medium">{day}</span>
              <div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center transition-all
                  ${isActive 
                    ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-md' 
                    : isFuture
                    ? 'bg-muted/30 border border-border'
                    : 'bg-muted border border-border'
                  }
                  ${isToday && !isActive ? 'ring-2 ring-orange-400 ring-offset-2 ring-offset-background' : ''}
                `}
              >
                {isActive && <Check className="w-4 h-4" />}
                {isToday && !isActive && <div className="w-2 h-2 bg-orange-500 rounded-full" />}
              </div>
            </div>
          );
        })}
      </div>

      {isGoalMet && (
        <div className="text-center pt-2 text-xs text-green-600 dark:text-green-400 font-medium">
          🎉 Goal achieved! Keep going!
        </div>
      )}
    </div>
  );
}
